import React, { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { Sparkles, Trophy, Users, ArrowRight, Play } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { membresiaService } from '../services/sorteoService'

const Hero = () => {
  const navigate = useNavigate()
  const [latestRaffle, setLatestRaffle] = useState(null)
  const [loading, setLoading] = useState(true)

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId)
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' })
    }
  }

  useEffect(() => {
    const fetchLatestRaffle = async () => {
      try {
        const raffles = await membresiaService.getAllMembresias()
        // Obtener el último sorteo activo
        const activeRaffles = raffles.filter(raffle => raffle.status === 'active')
        if (activeRaffles.length > 0) {
          setLatestRaffle(activeRaffles[0])
        }
      } catch (error) {
        console.error('Error al cargar el último sorteo:', error)
      } finally {
        setLoading(false)
      }
    }

    fetchLatestRaffle()
  }, [])

  const calculateTimeLeft = (endDate) => {
    if (!endDate) return '0 días 0h'
    const now = new Date()
    const end = new Date(endDate)
    const diff = end - now
    
    if (diff <= 0) return 'Finalizado'
    
    const days = Math.floor(diff / (1000 * 60 * 60 * 24))
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60))
    
    return `${days} días ${hours}h`
  }

  const getProgressPercentage = () => {
    if (!latestRaffle) return 0
    const sold = latestRaffle.boletas_vendidas || 0
    const total = latestRaffle.total_boletas || 100000
    return Math.min((sold / total) * 100, 100)
  }

  const formatNumber = (num) => {
    return new Intl.NumberFormat('es-CO').format(num)
  }

  return (
    <section id="inicio" className="relative min-h-screen flex items-center justify-center overflow-hidden pt-20 md:pt-0">
      {/* Background Effects */}
      <div className="absolute inset-0">
        <div className="absolute inset-0 bg-black/20" />
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-primary-500/10 rounded-full blur-3xl animate-pulse" />
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-secondary-500/10 rounded-full blur-3xl animate-pulse delay-1000" />
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-accent-500/5 rounded-full blur-3xl" />
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Content */}
          <div className="space-y-8">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="space-y-4"
            >
              <div className="inline-flex items-center space-x-2 bg-white/10 backdrop-blur-lg rounded-full px-4 py-2 border border-white/20">
                <Sparkles className="h-4 w-4 text-yellow-400" />
                <span className="text-sm text-white font-medium">¡Membresías Exclusivas Disponibles!</span>
              </div>
              
              <h1 className="text-5xl lg:text-7xl font-bold text-white leading-tight">
                Gana con
                <span className="block gradient-text font-display">
                  ParaisoTour
                </span>
              </h1>
              
              <p className="text-xl text-gray-300 max-w-2xl mx-auto lg:mx-0">
                Participa en membresías exclusivas con 100,000 números únicos. 
                Compra tus boletos, asegura tu participación y gana increíbles premios.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start"
            >
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="btn-primary flex items-center justify-center gap-2"
                onClick={() => scrollToSection('membresias-activas')}
              >
                <Trophy className="w-5 h-5" />
                Ver Membresías
              </motion.button>
              
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="btn-secondary flex items-center justify-center gap-2"
                onClick={() => scrollToSection('como-funciona')}
              >
                <Play className="w-5 h-5" />
                Cómo Funciona
              </motion.button>
            </motion.div>

            {/* Stats */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
              className="grid grid-cols-3 gap-8 pt-8"
            >
              <div className="text-center">
                <div className="text-3xl font-bold text-white mb-2">100K</div>
                <div className="text-sm text-gray-400">Números por Membresía</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-white mb-2">5K+</div>
                <div className="text-sm text-gray-400">Ganadores Felices</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-white mb-2">$2M+</div>
                <div className="text-sm text-gray-400">En Premios</div>
              </div>
            </motion.div>
          </div>

          {/* Membresía Premium */}
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1, delay: 0.3 }}
            className="relative"
          >
            <div className="card-modern p-6 relative overflow-hidden ring-2 ring-blue-400 bg-gradient-to-br from-blue-900/20 to-purple-900/20">
              <div className="space-y-6">
                <div className="relative">
                  <img 
                    src={latestRaffle?.image_url || "https://images.unsplash.com/photo-1592750475338-74b7b21085ab?w=400&h=200&fit=crop&crop=center"}
                    alt={latestRaffle?.name || "Membresía Premium"}
                    className="w-full h-60 object-cover rounded-lg shadow-xl"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent rounded-lg"></div>
                  <div className="absolute top-4 right-4 bg-gradient-to-r from-yellow-400 to-orange-400 text-black px-3 py-2 rounded-full text-sm font-bold z-10">
                    🏆 PREMIUM
                  </div>
                </div>
                
                {loading ? (
                  <div className="text-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white mx-auto mb-2"></div>
                    <p className="text-gray-400 text-sm">Cargando membresía...</p>
                  </div>
                ) : latestRaffle ? (
                  <>
                    <div>
                      <h3 className="text-xl font-bold gradient-text mb-2">Membresía Nueva</h3>
                      <p className="text-gray-300 text-sm">{latestRaffle.name}</p>
                    </div>
                    
                    <div className="space-y-3">
                      <div className="flex justify-between items-center">
                        <div className="text-center">
                          <div className="text-2xl font-bold text-blue-400">${formatNumber(latestRaffle.amount)} COP</div>
                          <div className="text-xs text-gray-400">por boleto</div>
                        </div>
                        <div className="text-center">
                          <div className="text-lg font-bold text-orange-400">{calculateTimeLeft(latestRaffle.end_date)}</div>
                          <div className="text-xs text-gray-400">restantes</div>
                        </div>
                      </div>
                      
                      <div>
                        <div className="flex justify-between text-xs mb-1">
                          <span className="text-gray-400">Vendidos: {formatNumber(latestRaffle.boletas_vendidas || 0)}</span>
                          <span className="text-gray-400">Total: {formatNumber(latestRaffle.total_boletas || 100000)}</span>
                        </div>
                        <div className="w-full bg-gray-700 rounded-full h-2">
                          <div 
                            className="bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500 h-2 rounded-full transition-all duration-300 pulse-glow"
                            style={{ width: `${getProgressPercentage()}%` }}
                          ></div>
                        </div>
                        <div className="text-xs text-gray-400 mt-1 text-center">
                          {Math.round(getProgressPercentage())}% vendido
                        </div>
                      </div>
                      
                      <motion.button
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        className="w-full btn-primary flex items-center justify-center gap-2"
                        onClick={() => navigate(`/membresia/${latestRaffle.id}`)}
                      >
                        <ArrowRight className="w-4 h-4" />
                        Comprar Membresía
                      </motion.button>
                    </div>
                  </>
                ) : (
                  <div className="text-center py-8">
                    <p className="text-gray-400 text-sm">No hay membresías activas</p>
                  </div>
                )}
              </div>
            </div>
            
            {/* Floating Elements - Outside the card */}
            <motion.div
              animate={{ y: [-10, 10, -10] }}
              transition={{ duration: 3, repeat: Infinity }}
              className="absolute -top-4 -right-4 bg-yellow-400 p-3 rounded-full shadow-lg z-20"
            >
              <Sparkles className="h-6 w-6 text-yellow-900" />
            </motion.div>
            
            <motion.div
              animate={{ y: [10, -10, 10] }}
              transition={{ duration: 3, repeat: Infinity, delay: 1.5 }}
              className="absolute -bottom-4 -left-4 bg-green-400 p-3 rounded-full shadow-lg z-20"
            >
              <Users className="h-6 w-6 text-green-900" />
            </motion.div>
          </motion.div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.5 }}
        className="absolute bottom-8 left-1/2 transform -translate-x-1/2"
      >
        <motion.div
          animate={{ y: [0, 10, 0] }}
          transition={{ duration: 2, repeat: Infinity }}
          className="w-6 h-10 border-2 border-white/30 rounded-full flex justify-center"
        >
          <div className="w-1 h-3 bg-white/50 rounded-full mt-2"></div>
        </motion.div>
      </motion.div>
    </section>
  )
}

export default Hero